import { type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from "react";
import { Css, Palette, type Properties } from "src/Css";
import { useTestIds } from "src/hooks";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost";
type ButtonSize = "small" | "medium" | "large" | "xl";
type ButtonState = "active" | "danger";
type ButtonWidth = "full" | "none";

type ButtonProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  state?: ButtonState;
  width?: ButtonWidth;
  iconLeft?: keyof typeof ButtonIcons;
  children: ReactNode;
  /** Optional onClick since when used with forms, onClick is not required */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  type?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  /**
   * Disables the button when given a truthy value. When given a string, a
   * Tooltip is shown with the string as the content.
   *
   * NOTE: To customize the tooltip place, change the tooltipPlacement prop.
   */
  disabled?: boolean | string;
  /**
   * Tooltip placement for tooltip and disabled reason.
   * @default "right"
   */
  openInNewTab?: boolean;
  /**
   * Prevents default and stops propagation of the event
   * @default false
   *
   * TODO: Should we be always stopping propagation and require an explicit
   * prop to allow propagation?
   * */
  stopPropagation?: boolean;
  /** Use for mapping when passing btn props */
  key?: string;
  /** Show spinner when loading */
  isLoading?: boolean;
  loadingText?: "Loading" | "Saving" | "Submitting";
};

export function Button(props: ButtonProps) {
  const {
    iconLeft,
    children,
    onClick,
    variant = "primary",
    size = "medium",
    disabled,
    stopPropagation = false,
    type,
    isLoading = false,
    width = "full",
    state,
  } = props;
  const tid = useTestIds("button");
  const isButtonDisabled = !!disabled || isLoading;

  function handleClick(e: MouseEvent<HTMLButtonElement>) {
    if (isButtonDisabled) {
      return;
    }
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    onClick?.(e);
  }

  return (
    <button
      css={{
        ...baseButtonStyles,
        ...sizeStyles[size],
        ...widthStyles[width],
        ...variantStyles[variant],
        ...(state ? stateStyles[state] : {}),
        ...Css.if(isButtonDisabled).cursorNotAllowed.$,
        ...Css.if(variant === "tertiary" && state === "active").bgGray900.bcGray900.whitePure.onHover.bgGray900.$,
        ...Css.if(variant === "primary" && isButtonDisabled).bgGray300.gray500.$,
        ...Css.if(variant === "tertiary" && isButtonDisabled).boxShadow(`0 0 0 1px ${Palette.Gray500}`).gray500.$,
        ...Css.if(variant === "ghost" && state === "danger").bgTransparent.red600.$,
      }}
      onClick={handleClick}
      disabled={isButtonDisabled}
      type={type}
      {...tid}
    >
      {iconLeft ? (
        <>
          {ButtonIcons[iconLeft]}
          {children}
        </>
      ) : (
        children
      )}
    </button>
  );
}

// Separating these from the other icons as they should be a standard of 16 x 16
const ButtonIcons = {
  plus: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="16" height="16" transform="translate(0 16) rotate(-90)" />
      <path
        d="M7.5 7.5L4 7.5L4 8.5L7.5 8.5L7.5 12L8.5 12L8.5 8.5L12 8.5L12 7.5L8.5 7.5L8.5 4L7.5 4L7.5 7.5Z"
        fill="currentColor"
      />
    </svg>
  ),
  trash: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.23333 14.1667C4.61749 14.1667 4.11667 13.6658 4.11667 13.05V6.35H3V5.23333H4.675H5.23333V4.11667C5.23333 3.50082 5.73416 3 6.35 3H9.69999C10.3158 3 10.8167 3.50082 10.8167 4.11667V5.23333H11.375H13.05V6.35H11.9333V13.05C11.9333 13.6658 11.4325 14.1667 10.8167 14.1667H5.23333ZM9.69999 4.11667H6.35V5.23333H9.69999V4.11667ZM10.2583 6.35H5.79166H5.23333V13.05H10.8172L10.8167 6.35H10.2583ZM7.46628 7.46667H6.34961V11.9333H7.46628V7.46667ZM9.69961 7.46667H8.58294V11.9333H9.69961V7.46667Z"
        fill="currentColor"
      />
    </svg>
  ),
};

const baseButtonStyles = Css.add("appearance", "none").bn.bgTransparent.br4.df.aic.jcc.gapPx(4).relative.wsnw
  .cursorPointer.$;

const sizeStyles: Record<ButtonSize, Properties> = {
  small: Css.px1.pyPx(3).f12.lh("18px").fw5.add("letterSpacing", "unset").$,
  medium: Css.pxPx(12).pyPx(7).f14.lh("18px").fw5.add("letterSpacing", "unset").$,
  large: Css.px2.pyPx(11).f14.lh("18px").fw5.add("letterSpacing", "unset").$,
  xl: Css.pxPx(20).pyPx(15).f16.lh("18px").fw5.add("letterSpacing", "unset").$,
};

const widthStyles: Record<ButtonWidth, Properties> = {
  full: Css.w100.$,
  none: {},
};

const variantStyles: Record<ButtonVariant, Properties> = {
  primary: Css.bgBlue600.whitePure.onHover.bgBlue800.$,
  secondary: Css.bgGray900.whitePure.onHover.bgBlack.$,
  tertiary: Css.gray900.bcGray300.boxShadow(`0 0 0 1px ${Palette.Black}`).onHover.bgOffWhite.$,
  ghost: Css.blue600.onHover.bgGray100.$,
};

const stateStyles: Record<ButtonState, Properties> = {
  active: Css.bgGray700.$,
  danger: Css.bgRed600.$,
};
